import React from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addClientFromRequest, getClientRequest, getWantAdById } from "@/services/wantAdService";
import { compactPrice } from "@/helpers/compactPrice";
import "./ClientRequestPanel.scss";

const range = (min, max, format) => {
  const low = Number(min) > 0 ? format(min) : null;
  const high = Number(max) > 0 ? format(max) : null;
  if (low && high) return `${low} to ${high}`;
  if (low) return `${low} or more`;
  if (high) return `Up to ${high}`;
  return null;
};

const sqft = (value) => `${Number(value).toLocaleString("en-US")} sqft`;

export const requestFacts = (ad) =>
  [
    ["Area", ad?.shareArea?.longLabel || ad?.location],
    ["Budget", range(ad?.minBdgt, ad?.maxBdgt, compactPrice)],
    ["Beds", Number(ad?.noBedRm) > 0 ? `${ad.noBedRm}+` : null],
    ["Baths", Number(ad?.noBathRm) > 0 ? `${ad.noBathRm}+` : null],
    ["Size", range(ad?.sqFtMin, ad?.sqFtMax, sqft)],
    ["Property type", (ad?.prptySubTypesTxt || []).filter(Boolean).join(", ") || ad?.prptyTypeTxt],
    ["Timeline", ad?.tlText],
    ["Financing", ad?.finTypeTxt],
    ["Must-haves", (ad?.nice2Have || []).map((item) => item?.text || item?.title || item).filter((item) => typeof item === "string").join(", ")],
  ].filter(([, value]) => value);

const sentOn = (value) => {
  const date = new Date(String(value || "").replace(" ", "T"));
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const resultOf = (response, fallback) => {
  if (response?.errorCode !== 0) throw new Error(response?.message || fallback);
  return response.result;
};

const errorText = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const formatPhone = (phone) => {
  const digits = String(phone || "").replace(/[^0-9]/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : String(phone || "");
};

const firstNameOf = (name) => String(name || "").trim().split(/\s+/)[0] || "";

const ClientRequestPanel = ({ request, onHide, onContinue }) => {
  const wantAdId = request?.Want_ad_Id;
  const queryClient = useQueryClient();
  const details = useQuery({
    queryKey: ["clientRequest", wantAdId],
    enabled: Boolean(wantAdId),
    queryFn: async () => resultOf(await getWantAdById(wantAdId), "We couldn't load this request."),
  });
  const sender = useQuery({
    queryKey: ["clientRequestSender", wantAdId],
    enabled: Boolean(wantAdId),
    queryFn: async () => resultOf(await getClientRequest(wantAdId), "We couldn't load who sent this."),
  });
  const addClient = useMutation({
    mutationFn: async () => resultOf(await addClientFromRequest(wantAdId), "We couldn't add this client."),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agentWantAds"] });
      queryClient.invalidateQueries({ queryKey: ["clientRequestSender", wantAdId] });
      onContinue(wantAdId);
    },
  });

  const ad = details.data;
  const contact = sender.data?.contact;
  const name = String(contact?.name || request?.RequesterName || ad?.clientName || "").trim();
  const firstName = firstNameOf(name);
  const linked = Boolean(sender.data?.clientId);
  const alreadyClient = Boolean(sender.data?.matchedClientId);
  const sent = sentOn(contact?.sentOn || ad?.createdOn || request?.CreatedOn);
  const title = ad?.adTitle || request?.Title_Text;
  const facts = requestFacts(ad);
  const ready = Boolean(ad) && sender.isSuccess;

  const primaryLabel = addClient.isPending
    ? `Adding ${firstName || "client"}...`
    : linked || !contact
      ? "Continue to publish"
      : `Add ${firstName || "client"} as a client and continue`;

  return (
    <Offcanvas show={Boolean(request)} onHide={onHide} placement="end" className="client-request-panel">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title as="div" className="client-request-panel__head">
          <span className="client-request-panel__kind">Buyer Listing Form request</span>
          <h2 className="client-request-panel__name">{name || "Someone on your Buyer Listing Form"}</h2>
          {sent ? <span className="client-request-panel__sent">Sent {sent}</span> : null}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="client-request-panel__body">
        {contact?.email || contact?.phone ? (
          <dl className="client-request-panel__contact">
            {contact.email ? (
              <div>
                <dt>Email</dt>
                <dd><a href={`mailto:${contact.email}`}>{contact.email}</a></dd>
              </div>
            ) : null}
            {contact.phone ? (
              <div>
                <dt>Phone</dt>
                <dd><a href={`tel:${contact.phone}`}>{formatPhone(contact.phone)}</a></dd>
              </div>
            ) : null}
          </dl>
        ) : null}
        {linked ? (
          <p className="client-request-panel__status">{firstName || "This person"} is in My Clients and linked to this request.</p>
        ) : alreadyClient ? (
          <p className="client-request-panel__status">{firstName || "This person"} is already in My Clients. We'll link this request to them.</p>
        ) : null}

        <section className="client-request-panel__section">
          <h3 className="client-request-panel__section-title">
            {firstName ? `What ${firstName} is looking for` : "What they're looking for"}
          </h3>
          {title ? <p className="client-request-panel__title">{title}</p> : null}
          {details.isError ? (
            <div className="client-request-panel__error" role="alert">
              <p>{details.error?.message || "We couldn't load this request."}</p>
              <button type="button" className="btn btn-secondary" onClick={() => details.refetch()}>
                Try again
              </button>
            </div>
          ) : details.isLoading ? (
            <div className="client-request-panel__facts" aria-busy="true" aria-label="Loading the request">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="client-request-panel__fact client-request-panel__fact--loading">
                  <span />
                  <span />
                </div>
              ))}
            </div>
          ) : (
            <dl className="client-request-panel__facts">
              {facts.map(([label, value]) => (
                <div key={label} className="client-request-panel__fact">
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        {contact?.message || ad?.adDesc ? (
          <section className="client-request-panel__section">
            <h3 className="client-request-panel__section-title">{firstName ? `${firstName}'s note` : "Their note"}</h3>
            <p className="client-request-panel__note">{contact?.message || ad?.adDesc}</p>
          </section>
        ) : null}

        <p className="client-request-panel__next">
          Next you&apos;ll add a title and a short description, then post it as a Buyer&nbsp;{"Want-\u2060Listing"}.
        </p>
      </Offcanvas.Body>
      <div className="client-request-panel__actions">
        {addClient.isError ? (
          <p className="client-request-panel__action-error" role="alert">
            {errorText(addClient.error, "We couldn't add this client. Please try again.")}
          </p>
        ) : null}
        <button type="button" className="btn btn-secondary" onClick={onHide}>
          Not now
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!ready || addClient.isPending}
          onClick={() => (linked || !contact ? onContinue(wantAdId) : addClient.mutate())}
        >
          {primaryLabel}
        </button>
      </div>
    </Offcanvas>
  );
};

export default ClientRequestPanel;
